import React, { useState, useEffect } from 'react';
import { 
  performBrowserCheck, 
  getBrowserRecommendation,
  detectInAppBrowser 
} from '../utils/browserCheck';

const BrowserCheckModal = ({ isOpen, onConfirm, onCancel, onForceClose }) => {
  const [checkResults, setCheckResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userAcknowledged, setUserAcknowledged] = useState(false);

  useEffect(() => {
    if (isOpen) {
      checkBrowser();
    }
  }, [isOpen]);

  const checkBrowser = async () => {
    setLoading(true);
    const results = await performBrowserCheck();
    setCheckResults(results);
    setLoading(false);
  };

  if (!isOpen) return null;

  const hasErrors = checkResults?.warnings.some(w => w.level === 'error');
  const hasWarnings = checkResults?.warnings.some(w => w.level === 'warning');
  const recommendation = getBrowserRecommendation();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-scale-in">
        <div className="p-6">
          {/* Заголовок */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900 flex items-center">
              {loading ? (
                <>
                  <svg className="w-7 h-7 mr-2 text-blue-600 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Проверка браузера...
                </>
              ) : hasErrors ? (
                <>
                  <svg className="w-7 h-7 mr-2 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  Внимание!
                </>
              ) : hasWarnings ? (
                <>
                  <svg className="w-7 h-7 mr-2 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  Предупреждение
                </>
              ) : (
                <>
                  <svg className="w-7 h-7 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Всё готово!
                </>
              )}
            </h2>
            {!hasErrors && onForceClose && (
              <button
                onClick={onForceClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>

          {loading ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-600">Проверяем ваш браузер и окружение...</p>
            </div>
          ) : (
            <>
              {/* Результаты проверки */}
              {checkResults?.warnings.length > 0 ? (
                <div className="space-y-4 mb-6">
                  {checkResults.warnings.map((warning, index) => (
                    <div
                      key={index}
                      className={`p-4 rounded-lg border-2 ${
                        warning.level === 'error'
                          ? 'bg-red-50 border-red-300'
                          : 'bg-yellow-50 border-yellow-300'
                      }`}
                    >
                      <div className="flex items-start">
                        <svg 
                          className={`w-6 h-6 flex-shrink-0 mr-3 ${
                            warning.level === 'error' ? 'text-red-600' : 'text-yellow-600'
                          }`} 
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        <div className="flex-1">
                          <h4 className={`font-semibold mb-1 ${
                            warning.level === 'error' ? 'text-red-900' : 'text-yellow-900'
                          }`}>
                            {warning.level === 'error' ? '🚫 Критическая проблема' : '⚠️ Предупреждение'}
                          </h4>
                          <p className={`text-sm mb-2 ${
                            warning.level === 'error' ? 'text-red-800' : 'text-yellow-800'
                          }`}>
                            {warning.message}
                          </p>
                          <p className={`text-sm font-medium ${
                            warning.level === 'error' ? 'text-red-700' : 'text-yellow-700'
                          }`}>
                            💡 {warning.action}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Инструкция для встроенного браузера */}
                  {checkResults.isInAppBrowser && (
                    <div className="bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-300 rounded-lg p-5">
                      <h4 className="font-bold text-blue-900 mb-3 flex items-center text-lg">
                        <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Как открыть в обычном браузере:
                      </h4>
                      
                      <div className="space-y-3 text-sm text-blue-900">
                        <div className="flex items-start">
                          <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-xs mr-3">1</span>
                          <p>Скопируйте адрес этой страницы (ссылку)</p>
                        </div>
                        <div className="flex items-start">
                          <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-xs mr-3">2</span>
                          <p>Нажмите на три точки ⋮ в правом верхнем углу</p>
                        </div>
                        <div className="flex items-start">
                          <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-xs mr-3">3</span>
                          <p>Выберите "Открыть в {recommendation.primary}" или "Открыть во внешнем браузере"</p>
                        </div>
                      </div>

                      <div className="mt-4 p-3 bg-white rounded-lg">
                        <p className="text-xs text-gray-600 mb-2">📋 Скопируйте эту ссылку:</p>
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={window.location.href}
                            readOnly
                            className="flex-1 px-3 py-2 text-xs bg-gray-50 border border-gray-300 rounded font-mono"
                          />
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(window.location.href);
                            }}
                            className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded transition-colors"
                          >
                            Копировать
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Рекомендованные браузеры */}
                  {hasErrors && (
                    <div className="bg-gray-50 border border-gray-300 rounded-lg p-4">
                      <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                        <svg className="w-5 h-5 mr-2 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Рекомендованные браузеры:
                      </h4>
                      <div className="space-y-2 text-sm text-gray-700">
                        <p>✅ <strong>{recommendation.primary}</strong> (рекомендуем)</p>
                        {recommendation.alternatives.map((alt, i) => (
                          <p key={i}>✅ {alt}</p>
                        ))}
                        <p className="text-xs text-gray-500 mt-2 italic">{recommendation.note}</p>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-green-50 border-2 border-green-300 rounded-lg p-6 mb-6">
                  <div className="flex items-start">
                    <svg className="w-8 h-8 text-green-600 mr-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <h3 className="text-lg font-bold text-green-900 mb-2">
                        Ваш браузер готов к работе!
                      </h3>
                      <p className="text-green-800 mb-3">
                        Все проверки пройдены успешно. Ваша история будет надёжно сохранена.
                      </p>
                      <div className="space-y-1 text-sm text-green-700">
                        <p>✅ Браузер: {checkResults?.browser.browserName} {checkResults?.browser.browserVersion}</p>
                        <p>✅ LocalStorage доступен</p>
                        <p>✅ Страница полностью загружена</p>
                        <p>✅ Обычный режим (не приватный)</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Чекбокс подтверждения для предупреждений */}
              {hasWarnings && !hasErrors && (
                <div className="mb-6">
                  <label className="flex items-start cursor-pointer">
                    <input
                      type="checkbox"
                      checked={userAcknowledged}
                      onChange={(e) => setUserAcknowledged(e.target.checked)}
                      className="mt-1 w-5 h-5 text-primary-600 rounded border-gray-300 focus:ring-2 focus:ring-primary-500"
                    />
                    <span className="ml-3 text-sm text-gray-700">
                      Я понимаю риски и хочу продолжить. Я сохраню подписку в безопасном месте (скопирую, отправлю на email или в Telegram).
                    </span>
                  </label>
                </div>
              )}

              {/* Важное предупреждение */}
              {(hasErrors || hasWarnings) && (
                <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-4 mb-6">
                  <div className="flex items-start">
                    <svg className="w-6 h-6 text-amber-600 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <h4 className="font-bold text-amber-900 mb-1">⚠️ Важно!</h4>
                      <p className="text-sm text-amber-800 leading-relaxed">
                        {hasErrors ? (
                          <>
                            <strong>История восстановлений НЕ СОХРАНИТСЯ</strong> в вашем браузере. 
                            После закрытия вы потеряете доступ к подписке. 
                            <strong className="text-amber-900"> Обязательно скопируйте подписку</strong> или 
                            укажите email для отправки резервной копии!
                          </>
                        ) : (
                          <>
                            Обнаружены некоторые проблемы с вашим браузером. 
                            Рекомендуем <strong>указать email</strong> для получения резервной копии подписки.
                          </>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Детальная информация */}
              {!loading && checkResults && (
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <h4 className="font-semibold text-gray-900 mb-3 text-sm">📋 Детали проверки:</h4>
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div className="flex items-center">
                      {checkResults.localStorage ? '✅' : '❌'}
                      <span className="ml-2 text-gray-700">LocalStorage</span>
                    </div>
                    <div className="flex items-center">
                      {checkResults.pageLoaded ? '✅' : '❌'}
                      <span className="ml-2 text-gray-700">Страница загружена</span>
                    </div>
                    <div className="flex items-center">
                      {!checkResults.isInAppBrowser ? '✅' : '❌'}
                      <span className="ml-2 text-gray-700">Обычный браузер</span>
                    </div>
                    <div className="flex items-center">
                      {!checkResults.privateMode ? '✅' : '❌'}
                      <span className="ml-2 text-gray-700">Не приватный режим</span>
                    </div>
                    <div className="flex items-center">
                      {checkResults.apis.fetch ? '✅' : '❌'}
                      <span className="ml-2 text-gray-700">Fetch API</span>
                    </div>
                    <div className="flex items-center">
                      {checkResults.apis.clipboard ? '✅' : '❌'}
                      <span className="ml-2 text-gray-700">Clipboard API</span>
                    </div>
                  </div>
                  
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <p className="text-xs text-gray-600">
                      <strong>Браузер:</strong> {checkResults.browser.browserName} {checkResults.browser.browserVersion}
                    </p>
                    {checkResults.isInAppBrowser && (
                      <p className="text-xs text-red-600 font-medium mt-1">
                        <strong>Встроенный браузер:</strong> {checkResults.inAppBrowserName}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Кнопки действий */}
              <div className="flex flex-col sm:flex-row gap-3">
                {hasErrors ? (
                  <>
                    <button
                      onClick={onCancel}
                      className="flex-1 px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors"
                    >
                      Отмена
                    </button>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(window.location.href);
                        alert('Ссылка скопирована! Откройте её в обычном браузере.');
                      }}
                      className="flex-1 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
                    >
                      Скопировать ссылку
                    </button>
                  </>
                ) : hasWarnings ? (
                  <>
                    <button
                      onClick={onCancel}
                      className="flex-1 px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg font-medium transition-colors"
                    >
                      Отмена
                    </button>
                    <button
                      onClick={onConfirm}
                      disabled={!userAcknowledged}
                      className={`flex-1 px-6 py-3 rounded-lg font-medium transition-colors ${
                        userAcknowledged
                          ? 'bg-yellow-600 hover:bg-yellow-700 text-white'
                          : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      }`}
                    >
                      Продолжить на свой риск
                    </button>
                  </>
                ) : (
                  <button
                    onClick={onConfirm}
                    className="w-full px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
                  >
                    ✅ Отлично! Продолжить
                  </button>
                )}
              </div>

              {/* Дополнительная информация */}
              {hasErrors && (
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-xs text-blue-800">
                    <strong>💡 Совет:</strong> Мы не можем продолжить, пока вы не откроете сайт в обычном браузере. 
                    Это необходимо для сохранения вашей истории восстановлений.
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BrowserCheckModal;











